import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const NOTIFICATIONS_COLLECTION = 'Hugo_Notifications';

export function NotificationProvider({ children }) {
  const { user } = useAuth();

  // Snackbar (toast) state - existing functionality
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  // In-app notifications state - new functionality
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  // Snackbar notify function (existing behavior)
  const notify = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Subscribe to Hugo_Notifications for the current user
  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Query notifications for this user, ordered by most recent first, limit 50
    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          // Normalize createdAt to JS Date for display
          createdAt: d.data().createdAt?.toDate?.() || new Date(),
        }));
        setNotifications(notifs);
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to notifications:', error);
        // Also try querying without userId filter (for org-wide notifications)
        // Fall back to empty array on error
        setNotifications([]);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  // Calculate unread count
  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.read).length;
  }, [notifications]);

  /**
   * Add a new notification to Firestore
   * @param {Object} params
   * @param {string} params.message - Notification message
   * @param {string} [params.type] - Notification type (e.g., 'threshold_exceeded', 'survey_created', 'info')
   * @param {string} [params.link] - Optional URL path to navigate to
   * @param {string} [params.userId] - Target user ID (defaults to current user)
   * @param {string} [params.organizationId] - Associated organization ID
   */
  const addNotification = useCallback(
    async ({ message, type = 'info', link = null, userId = null, organizationId = null }) => {
      try {
        const notifData = {
          userId: userId || user?.uid,
          message,
          type,
          link,
          organizationId,
          read: false,
          createdAt: Timestamp.now(),
        };

        await addDoc(collection(db, NOTIFICATIONS_COLLECTION), notifData);
      } catch (error) {
        console.error('Failed to add notification:', error);
      }
    },
    [user?.uid]
  );

  /**
   * Mark a notification as read
   * @param {string} notificationId - The Firestore document ID
   */
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await updateDoc(doc(db, NOTIFICATIONS_COLLECTION, notificationId), {
        read: true,
        readAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      const unreadNotifs = notifications.filter((n) => !n.read);
      await Promise.all(
        unreadNotifs.map((n) =>
          updateDoc(doc(db, NOTIFICATIONS_COLLECTION, n.id), {
            read: true,
            readAt: Timestamp.now(),
          })
        )
      );
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  }, [notifications]);

  const value = {
    // Snackbar (toast) - existing
    notify,
    // In-app notifications - new
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    loading,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </NotificationContext.Provider>
  );
}

/**
 * Hook for snackbar/toast notifications (existing - backward compatible)
 */
export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}

/**
 * Hook for in-app notification system (notifications array, unread count, etc.)
 */
export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;
